import { createApiClient } from "../apiService";
import { SuccessType } from "../commonTypes";
import {
  addIndexationLogosToPublicationTypes,
  addIndexationLogoToPublicationTypes,
  dimensionsTypes,
  indexation_logoTypes,
  renameArticleTypesTypes,
} from "./dimensionTypes";
import { affiliationTypes } from "../affiliation/affiliationTypes";
import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { useToken } from "../tokenUtils";

const apiClient = createApiClient("");
const domain = getSubdomain();

const handleResponse = <T>(response: any): T => {
  return response.data;
};
const handlePostRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const getArticleTypes = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    `${domain}/article-types`,
  );
  return handleResponse(response);
};

const getAllArticleTypes = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    `${domain}/article-types-all`,
  );
  return handleResponse(response);
};

// DELETE y PATCH responden con la lista completa (activos e inactivos)
const deleteArticleTypes = async (name: string): Promise<dimensionsTypes[]> => {
  const token = useToken();

  const response = await apiClient.delete<dimensionsTypes[]>(
    `${domain}/article-types`,
    {
      params: { name },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return handleResponse(response);
};

const renameArticleTypes = async (
  renameArticleTypesRequest: renameArticleTypesTypes,
): Promise<dimensionsTypes[]> => {
  const token = useToken();

  const response = await apiClient.patch<dimensionsTypes[]>(
    `${domain}/article-types`,
    {},
    {
      params: {
        name: renameArticleTypesRequest.name,
        new_name: renameArticleTypesRequest.new_name,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return handleResponse(response);
};

// POST responde con la lista de tipos activos
const addArticleTypes = async (name: string): Promise<dimensionsTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error("Faltan token");
  }

  const response = await apiClient.post<dimensionsTypes[]>(
    `${domain}/article-types`,
    {},
    {
      params: { name },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return handleResponse(response);
};

const getArticleStatus = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_status",
  );
  return handleResponse(response);
};

const getReviewStatus = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/review_status",
  );
  return handleResponse(response);
};

const getArticleAuthorContributions = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_author_contribution_types",
  );
  return handleResponse(response);
};

const getPublicationReviewType = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/publication_review_type",
  );
  return handleResponse(response);
};

const getContributionList = async (): Promise<dimensionsTypes[]> => {
  const response = await apiClient.get<dimensionsTypes[]>(
    "dimension/article_author_contribution_types",
  );
  return handleResponse(response);
};

const getAffiliations = async (): Promise<affiliationTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await apiClient.get<affiliationTypes[]>("/affiliation/", {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return handleResponse(response);
};

const getIndexationLogo = async (): Promise<indexation_logoTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await apiClient.get<indexation_logoTypes[]>(
    `indexation_logo/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return handleResponse(response);
};

const addIndexationLogoToPublication = async (
  addIndexationLogoToPublicationRequest: addIndexationLogoToPublicationTypes,
): Promise<{ status: string; rel_id: number }> => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await handlePostRequest(
    `indexation_logo/${addIndexationLogoToPublicationRequest.publication_slug}/add_indexation/${addIndexationLogoToPublicationRequest.id_indexation_logo}`,
    {},
    token,
  );
  return response;
};

// Reemplaza las indexaciones de la publicación y devuelve los logos resultantes
const addIndexationLogosToPublication = async (
  addIndexationLogosToPublicationRequest: addIndexationLogosToPublicationTypes,
): Promise<indexation_logoTypes[]> => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const res = await apiClient.put<indexation_logoTypes[]>(
    `indexation_logo/${addIndexationLogosToPublicationRequest.publication_slug}/add_indexations`,
    addIndexationLogosToPublicationRequest.id_indexation_logo_list,
    { headers },
  );

  return res.data;
};

const removeIndexationLogoFromPublication = async (
  removeIndexationLogoFromPublicationRequest: addIndexationLogoToPublicationTypes,
): Promise<SuccessType> => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await apiClient.delete<SuccessType>(
    `indexation_logo/${removeIndexationLogoFromPublicationRequest.publication_slug}/remove_indexation/${removeIndexationLogoFromPublicationRequest.id_indexation_logo}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return handleResponse(response);
};

const removeIndexationLogosFromPublication = async (
  removeIndexationLogosFromPublicationRequest: addIndexationLogosToPublicationTypes,
): Promise<SuccessType> => {
  const token = useToken();
  if (!token) throw new Error("Falta token");

  const response = await apiClient.request({
    url: `indexation_logo/${removeIndexationLogosFromPublicationRequest.publication_slug}/remove_indexations`,
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
    data: removeIndexationLogosFromPublicationRequest.id_indexation_logo_list,
  });
  return handleResponse<SuccessType>(response);
};

const getIndexationLogosByPublication = async (
  publication_slug: string | null = domain,
): Promise<indexation_logoTypes[]> => {
  const response = await apiClient.get<indexation_logoTypes[]>(
    `indexation_logo/${publication_slug}/logos`,
  );
  return handleResponse(response);
};

const dimensionsService = {
  getArticleTypes,
  getAllArticleTypes,
  deleteArticleTypes,
  renameArticleTypes,
  addArticleTypes,
  getArticleStatus,
  getReviewStatus,
  getArticleAuthorContributions,
  getPublicationReviewType,
  getContributionList,
  getAffiliations,
  getIndexationLogo,
  addIndexationLogoToPublication,
  addIndexationLogosToPublication,
  removeIndexationLogoFromPublication,
  removeIndexationLogosFromPublication,
  getIndexationLogosByPublication,
};

export default dimensionsService;
